import { ChangeEvent } from "react";

import { Box, Chip, Grid, MenuItem, Typography } from "@mui/material";
import { alpha } from "@mui/material";
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { CardCore } from "src/Pages/components/CardCore/CardCore";
import { DropZoneCore } from "src/Pages/components/DropZoneCore/DropZoneCore";
import { ButtonCore } from "src/Pages/components/ButtonCore/ButtonCore";
import { AppTextField } from "src/Pages/components";
import { DriveFolderUploadIcon, SearchIcon } from "src/Pages/components/Icons/Icons";

import { useGetOperatorsImport } from "src/shared/Hooks/useGetOperatorsImport";
import { UseFormUploadImportInstallments } from "./Hooks/UseFormUploadCommission";
import { useContextParcelas } from "src/Contexts/Parcelas/ParcelasContext";
import { IFilesUploadImportInstallments } from "src/Contexts/Parcelas/ParcelasTypes";
import { LoadingAnaliseImportInstallments } from "./LoadingAnaliseImportInstallments";
import { valuesDefaultUploadImportInstallments } from "src/Contexts/Parcelas/valuesDefaultInputsParcelas/ValueDefaultInputParcelas";

interface IUploadInstallment {
  step: number;
  setStep: (step: number) => void;
}

export const FormUploadInstallment = ({ setStep }: IUploadInstallment) => {
  const { filesUploadImportInstallments, setFilesUploadImportInstallments } = useContextParcelas();

  const { handleUploadCommission, handleCancelUpload, loading: loadingUpload } = UseFormUploadImportInstallments({ setStep });

  const { data: updatedResultListOperators, loading } = useGetOperatorsImport();

  const handleChange =
    (prop: keyof IFilesUploadImportInstallments) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        setFilesUploadImportInstallments((prev) => ({
          ...prev,
          [prop]: event.target.value,
        }));
      };

  return (
    <>
      <LoadingAnaliseImportInstallments
        open={loadingUpload}
        fileName={filesUploadImportInstallments?.file?.name}
        onCancel={handleCancelUpload}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} sx={{ flex: 1 }}>
          <CardCore sxStyle={{ m: 0, height: "100%" }}>
            <DropZoneCore
              onFilesChange={(files) => setFilesUploadImportInstallments({ ...filesUploadImportInstallments, file: files[0] })}
              accept={{
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
                "application/vnd.ms-excel": [],
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
                "text/plain": [],
                "text/csv": [],
              }}
              sxStyle={{
                height: "350px",
                padding: "8px",

                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",

                cursor: "pointer",
                background: (theme) => filesUploadImportInstallments?.file.name
                  ? alpha(theme.palette.success.main, 0.08)
                  : alpha(theme.palette.primary.main, 0.05),

                borderRadius: "12px",
                border: (theme) => filesUploadImportInstallments?.file.name
                  ? `2px solid ${theme.palette.success.main}`
                  : `1px dashed ${theme.palette.primary.main}`,

                transition: "all 0.3s ease",

                "&:hover": {
                  background: (theme) => filesUploadImportInstallments?.file.name
                    ? alpha(theme.palette.success.main, 0.12)
                    : alpha(theme.palette.primary.main, 0.1),
                  borderColor: (theme) => filesUploadImportInstallments?.file.name
                    ? theme.palette.success.main
                    : theme.palette.primary.dark,
                  transform: "scale(1.01)",
                  boxShadow: (theme) => `0 4px 20px ${alpha(filesUploadImportInstallments?.file ? theme.palette.success.main : theme.palette.primary.main, 0.15)}`,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  marginBottom: "16px",
                  background: (theme) => filesUploadImportInstallments?.file.name
                    ? alpha(theme.palette.success.main, 0.2)
                    : alpha(theme.palette.primary.main, 0.3),
                  transition: "all 0.3s ease",
                }}
              >
                {filesUploadImportInstallments?.file ? (
                  <CheckCircleIcon fontSize="large" sx={{ color: "success.main" }} />
                ) : (
                  <DriveFolderUploadIcon fontSize="large" />
                )}
              </Box>

              {filesUploadImportInstallments?.file.name ? (
                <>
                  <Typography sx={{ mb: 0.5, fontWeight: 600, color: "success.main" }}>
                    Arquivo anexado com sucesso!
                  </Typography>

                  <Typography sx={{ mb: 1, fontSize: "0.9rem", color: "text.primary" }}>
                    <Chip label={filesUploadImportInstallments.file.name} variant="filled" color="success" />
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                    Clique ou arraste para substituir
                  </Typography>
                </>
              ) : (
                <>
                  <Typography sx={{ mb: 1 }}>
                    <Typography component="span" sx={{ color: (theme) => theme.palette.primary.main }}>
                      Clique aqui
                    </Typography>{" "}
                    ou arraste seu arquivo
                  </Typography>
                  <Typography color="text.secondary">
                    Suporta arquivos .xlsx, .xls ou .txt
                  </Typography>
                </>
              )}
            </DropZoneCore>
          </CardCore>
        </Grid>

        <Grid item xs={12} md={6} sx={{ flex: 1 }}>
          <CardCore sxStyle={{ m: 0, height: "100%" }}>
            <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Parâmetros da análise
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Configure os parâmetros para análise do arquivo
                </Typography>
              </Box>
              <ButtonCore
                title="Limpar tudo"
                variant="outlined"
                size="small"
                onClick={() => {
                  setFilesUploadImportInstallments(valuesDefaultUploadImportInstallments);
                }}
              />
            </Box>

            <Box >
              <Typography mb={1.5} sx={{ fontWeight: 600 }}>Passo 1  -  Tipo de Arquivo</Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 5 }}>
                <ButtonCore
                  title="EXCEL"
                  variant={filesUploadImportInstallments?.type === "xlsx" ? "contained" : "outlined"}
                  fullWidth
                  disabled={loading}
                  startIcon={<DescriptionIcon />}

                  onClick={() => {
                    if (filesUploadImportInstallments?.type !== "xlsx") {
                      setFilesUploadImportInstallments((prev) => ({
                        ...prev,
                        operadora_id: ""
                      }))
                    }
                    setFilesUploadImportInstallments((prev) => ({
                      ...prev,
                      type: "xlsx"
                    }))
                  }}
                />

                <ButtonCore
                  title="TXT"
                  disabled={loading}
                  startIcon={<DescriptionIcon />}
                  variant={filesUploadImportInstallments?.type === "txt" ? "contained" : "outlined"}
                  fullWidth

                  onClick={() => {
                    if (filesUploadImportInstallments?.type !== "txt") {
                      setFilesUploadImportInstallments((prev) => ({
                        ...prev,
                        operadora_id: ""
                      }))
                    }
                    setFilesUploadImportInstallments((prev) => ({
                      ...prev,
                      type: "txt"
                    }))
                  }}
                />
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    select
                    variant="outlined"
                    id="operadora"
                    label="Operadora *"
                    fullWidth
                    disabled={loading || !filesUploadImportInstallments?.type}
                    sxStyle={{ opacity: "", pointerEvents: "auto", m: 0 }}
                    value={filesUploadImportInstallments?.operadora_id || ""}
                    onChange={handleChange("operadora_id")}
                  >
                    {updatedResultListOperators?.filter((option) => option.layouts.some((layout) => layout.file_type === filesUploadImportInstallments?.type)).length === 0 ?
                      (
                        <MenuItem disabled value="">
                          Nenhum
                        </MenuItem>
                      ) :
                      (
                        updatedResultListOperators?.filter((option) => option.layouts.some((layout) => layout.file_type === filesUploadImportInstallments?.type)).map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.name}
                          </MenuItem>
                        ))
                      )}

                  </AppTextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AppTextField
                    variant="outlined"
                    label="Data de Recebimento *"
                    fullWidth
                    sxStyle={{ opacity: "", pointerEvents: "auto", m: 0 }}
                    type="date"
                    value={filesUploadImportInstallments?.data || ""}
                    onChange={handleChange("data")}
                  />
                </Grid>
              </Grid>

              <Typography color="text.secondary" sx={{ mb: 0.9, fontSize: "0.9rem" }}>
                Clique no botão para iniciar a análise do arquivo.
              </Typography>

              <ButtonCore
                startIcon={<SearchIcon />}
                title={loadingUpload ? "Analisando..." : "Analisar"}
                variant="contained"
                fullWidth
                disabled={loadingUpload}
                onClick={handleUploadCommission}
              />
            </Box>
          </CardCore>
        </Grid>
      </Grid>
    </>
  );
};